import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Combobox } from '../ui/combobox';
import {
  Plus, Check, Loader2, Calendar, User, Wrench, Package, Trash2, ShoppingCart, ScanLine
} from 'lucide-react';
import BarcodeScanner from './BarcodeScanner';

const DispensacaoAvulsoTab = ({
  categoria, cart, records, loading,
  estoqueItens, pacienteOptions, usuarios,
  avulsoForm, setAvulsoForm, savingAvulso,
  onAddToCart, onRemoveFromCart, onFinalizarDispensacao,
  selectedDate, setSelectedDate, onRefresh,
  onBarcodeScan, scanResult, scanLoading,
}) => {
  const label = categoria === 'ambulancias' ? 'Ambulâncias' : 'Base';
  const colorBg = categoria === 'ambulancias' ? 'bg-teal-600 hover:bg-teal-700' : 'bg-indigo-600 hover:bg-indigo-700';
  const colorIcon = categoria === 'ambulancias' ? 'text-teal-600' : 'text-indigo-600';
  const colorCircle = categoria === 'ambulancias' ? 'bg-teal-100 dark:bg-teal-900/30' : 'bg-indigo-100 dark:bg-indigo-900/30';

  return (
    <>
      {/* Form + Cart */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className={`h-4 w-4 ${colorIcon}`} />
            Dispensar MAT/MED {label}
          </CardTitle>
          <CardDescription>
            Adicione itens ao carrinho e finalize a dispensação de uma vez
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Barcode Scanner */}
          <div className="p-3 rounded-lg border-2 border-dashed border-primary/20 bg-primary/5">
            <div className="flex items-center gap-2 mb-2">
              <ScanLine className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Leitura rápida por código de barras</span>
            </div>
            <BarcodeScanner
              onScan={onBarcodeScan}
              result={scanResult}
              loading={scanLoading}
              placeholder="Escaneie o SKU para adicionar ao carrinho..."
            />
          </div>

          {/* Técnico + Paciente */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-sm flex items-center gap-1">
                <User className="h-3 w-3" /> Técnico responsável *
              </Label>
              <Combobox
                options={usuarios.map(u => ({ value: u.id || u.nome, label: u.nome, subtitle: u.perfil }))}
                value={avulsoForm.tecnico_id || avulsoForm.tecnico_nome}
                onValueChange={(v) => {
                  const usr = usuarios.find(u => (u.id || u.nome) === v);
                  setAvulsoForm(f => ({ ...f, tecnico_nome: usr?.nome || v, tecnico_id: v }));
                }}
                placeholder="Selecionar técnico..."
                searchPlaceholder="Buscar por nome..."
                emptyText="Nenhum usuário encontrado"
                data-testid={`select-${categoria}-tecnico`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Vincular a paciente (opcional)</Label>
              <Combobox
                options={pacienteOptions}
                value={avulsoForm.internacao_id}
                onValueChange={(v) => setAvulsoForm(f => ({ ...f, internacao_id: v }))}
                placeholder="Sem vínculo (retirada geral)"
                searchPlaceholder="Buscar paciente..."
                emptyText="Nenhum paciente encontrado"
                data-testid={`select-${categoria}-paciente`}
              />
            </div>
          </div>

          {/* Add item row */}
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end border-t pt-4">
            <div className="sm:col-span-5 space-y-1">
              <Label className="text-sm">Item *</Label>
              <Combobox
                options={estoqueItens.map(i => ({
                  value: i.id,
                  label: `${i.nome} (${i.estoque_atual} ${i.unidade})`,
                  subtitle: i.tipo === 'medicamento' ? 'Medicamento' : 'Material',
                }))}
                value={avulsoForm.item_id}
                onValueChange={(v) => setAvulsoForm(f => ({ ...f, item_id: v }))}
                placeholder="Buscar item..."
                searchPlaceholder="Digite o nome..."
                emptyText="Nenhum item encontrado"
                data-testid={`select-${categoria}-item`}
              />
            </div>
            <div className="sm:col-span-2 space-y-1">
              <Label className="text-sm">Qtd *</Label>
              <Input
                type="number" step="1" min="1"
                value={avulsoForm.quantidade}
                onChange={(e) => setAvulsoForm(f => ({ ...f, quantidade: e.target.value }))}
                data-testid={`input-${categoria}-quantidade`}
              />
            </div>
            <div className="sm:col-span-3 space-y-1">
              <Label className="text-sm">Obs</Label>
              <Input
                value={avulsoForm.observacoes}
                onChange={(e) => setAvulsoForm(f => ({ ...f, observacoes: e.target.value }))}
                placeholder="Ex: Acesso venoso..."
                data-testid={`input-${categoria}-obs`}
              />
            </div>
            <div className="sm:col-span-2">
              <Button
                variant="outline" className="w-full gap-1"
                onClick={() => onAddToCart(categoria)}
                disabled={!avulsoForm.item_id}
                data-testid={`btn-add-${categoria}`}
              >
                <Plus className="h-4 w-4" /> Adicionar
              </Button>
            </div>
          </div>

          {/* Cart */}
          {cart.length > 0 && (
            <div className="border rounded-lg overflow-hidden mt-2">
              <div className="bg-muted/50 px-4 py-2 flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                <span className="text-sm font-medium">Carrinho ({cart.length} {cart.length === 1 ? 'item' : 'itens'})</span>
              </div>
              <div className="divide-y">
                {cart.map((entry) => (
                  <div key={entry._cartId} className="flex items-center justify-between px-4 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {entry.item_tipo === 'medicamento' ? 'MED' : 'MAT'}
                      </Badge>
                      <span className="text-sm font-medium">{entry.item_nome}</span>
                      <span className="text-xs text-muted-foreground">x{entry.quantidade} {entry.item_unidade}</span>
                      {entry.observacoes && <span className="text-xs text-muted-foreground italic">({entry.observacoes})</span>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => onRemoveFromCart(categoria, entry._cartId)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="bg-muted/50 px-4 py-3">
                <Button
                  onClick={() => onFinalizarDispensacao(categoria)}
                  disabled={savingAvulso || !avulsoForm.tecnico_nome}
                  className={`w-full ${colorBg}`}
                  data-testid={`btn-finalizar-${categoria}`}
                >
                  {savingAvulso ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                  Finalizar dispensação ({cart.length} {cart.length === 1 ? 'item' : 'itens'})
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Date filter */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <div className="space-y-1 w-48">
              <Label className="text-sm font-medium flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Data</Label>
              <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} data-testid={`input-data-${categoria}`} />
            </div>
            <div className="flex items-end pt-5">
              <Button variant="outline" size="sm" onClick={onRefresh}>Atualizar</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Records */}
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : records.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Registros do dia ({records.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {records.map((d) => (
                <div key={d.id} className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-full ${colorCircle} flex items-center justify-center`}>
                      <Wrench className={`h-4 w-4 ${colorIcon}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{d.item_nome}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                        <span>Qtd: {d.quantidade}</span>
                        <span>|</span>
                        <span className="flex items-center gap-1"><User className="h-3 w-3" />{d.tecnico_nome}</span>
                        {d.paciente_nome && (<><span>|</span><span>Paciente: {d.paciente_nome}</span></>)}
                        {d.observacoes && (<><span>|</span><span className="italic">{d.observacoes}</span></>)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {d.internacao_id ? (
                      <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">Paciente</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs border-gray-300 text-gray-600">Geral</Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {d.criado_em ? new Date(d.criado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Wrench className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>Nenhum registro de {label} nesta data</p>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default DispensacaoAvulsoTab;
